import { useState } from 'react';
import { useFormat } from '@/hooks/useAccess';
import { useSupplierQuery, useSupplierStatementQuery } from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatMoney } from '@/lib/format';
import {
  Badge, Button, ErrorState, LoadingState, Modal, Pagination, Table, Td, Th,
} from '@/components/ui';

const ENTRY_TONE: Record<string, 'neutral' | 'success' | 'warning' | 'danger' | 'brand'> = {
  OPENING: 'neutral',
  INVOICE: 'warning',
  PAYMENT: 'success',
  DEBIT_NOTE: 'brand',
  REFUND: 'danger',
  ADJUSTMENT: 'neutral',
};

/**
 * A supplier's account. The columns are labelled from our side of the
 * relationship — their invoice is what we are "billed", our payment is what we
 * "paid" — because "debit" and "credit" read backwards to most people here.
 */
export function SupplierDetailModal({
  supplierId,
  onClose,
}: {
  supplierId: string;
  onClose: () => void;
}) {
  const format = useFormat();
  const [page, setPage] = useState(1);

  const { data: supplier, isLoading, isError, error, refetch } = useSupplierQuery(supplierId);
  const statement = useSupplierStatementQuery({ id: supplierId, params: { page, limit: 15 } });

  return (
    <Modal
      open
      onClose={onClose}
      title={supplier ? supplier.name : 'Supplier'}
      description={supplier ? supplier.code : undefined}
      size="lg"
      footer={<Button variant="outline" onClick={onClose}>Close</Button>}
    >
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
      ) : supplier ? (
        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-subtle p-3">
              <p className="text-xs text-muted">We owe</p>
              <p
                className={`text-lg font-semibold ${
                  Number(supplier.balance) > 0 ? 'text-amber-600' : ''
                }`}
              >
                {formatMoney(supplier.balance, format)}
              </p>
            </div>
            <div className="rounded-lg border border-subtle p-3">
              <p className="text-xs text-muted">Payment terms</p>
              <p className="text-lg font-semibold">
                {supplier.paymentTermDays > 0 ? `${supplier.paymentTermDays} days` : 'On demand'}
              </p>
            </div>
            <div className="rounded-lg border border-subtle p-3">
              <p className="text-xs text-muted">Orders</p>
              <p className="text-lg font-semibold">{supplier._count?.purchaseOrders ?? 0}</p>
            </div>
          </div>

          <h3 className="text-sm font-medium">Statement</h3>

          <Table>
            <thead>
              <tr>
                <Th>Date</Th>
                <Th>Entry</Th>
                <Th>Reference</Th>
                <Th numeric>Billed</Th>
                <Th numeric>Paid</Th>
                <Th numeric>Balance</Th>
              </tr>
            </thead>
            <tbody className={statement.isFetching ? 'opacity-60' : undefined}>
              {statement.data?.items.map((entry) => (
                <tr key={entry.id}>
                  <Td className="text-secondary">{formatDate(entry.occurredAt, format)}</Td>
                  <Td>
                    <Badge tone={ENTRY_TONE[entry.entryType] ?? 'neutral'}>
                      {entry.entryType.toLowerCase().replace(/_/g, ' ')}
                    </Badge>
                  </Td>
                  <Td className="text-secondary">
                    {entry.referenceNumber ?? entry.description ?? '—'}
                  </Td>
                  <Td numeric className="text-secondary">
                    {Number(entry.credit) > 0 ? formatMoney(entry.credit, format) : '—'}
                  </Td>
                  <Td numeric className="text-secondary">
                    {Number(entry.debit) > 0 ? formatMoney(entry.debit, format) : '—'}
                  </Td>
                  <Td numeric className="font-medium">
                    {formatMoney(entry.balanceAfter, format)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>

          {statement.data && statement.data.items.length === 0 && (
            <p className="text-sm text-muted">
              Nothing on the account yet. Invoices, payments and returns appear here.
            </p>
          )}

          {statement.data && (
            <Pagination
              page={statement.data.meta.page}
              totalPages={statement.data.meta.totalPages}
              total={statement.data.meta.total}
              onPageChange={setPage}
            />
          )}
        </div>
      ) : null}
    </Modal>
  );
}
